import type { Progress } from '../types'

// 单个勋章的静态定义
export interface AchievementDef {
  id: string
  name: string
  description: string
  imagePath: string           // /assets/achievements/xxx.jpg
  category: 'chapter' | 'performance' | 'persistence'
}

export const ACHIEVEMENTS: AchievementDef[] = [
  // ============ 章节勋章 ============
  {
    id: 'first_card',
    name: '初出茅庐',
    description: '完成第 1 张卡片',
    imagePath: '/assets/achievements/first_card.jpg',
    category: 'chapter',
  },
  {
    id: 'ch1_master',
    name: '四不原则',
    description: 'Ch1「先活下来」全部 mastery ≥ 60',
    imagePath: '/assets/achievements/ch1_master.jpg',
    category: 'chapter',
  },
  {
    id: 'ch2_master',
    name: '信号猎手',
    description: 'Ch2「买卖信号识别」全部 mastery ≥ 60',
    imagePath: '/assets/achievements/ch2_master.jpg',
    category: 'chapter',
  },
  {
    id: 'ch3_master',
    name: '仓位大师',
    description: 'Ch3「仓位管理」全部 mastery ≥ 60',
    imagePath: '/assets/achievements/ch3_master.jpg',
    category: 'chapter',
  },
  {
    id: 'ch4_master',
    name: '复盘王',
    description: 'Ch4「复盘与行为统计」全部 mastery ≥ 60',
    imagePath: '/assets/achievements/ch4_master.jpg',
    category: 'chapter',
  },
  {
    id: 'ch5_master',
    name: '题材通',
    description: 'Ch5「行业与题材识别」全部 mastery ≥ 60',
    imagePath: '/assets/achievements/ch5_master.jpg',
    category: 'chapter',
  },
  {
    id: 'chapter_unlock_5',
    name: '章节破壁者',
    description: '解锁第 5 章「行业与题材识别」',
    imagePath: '/assets/achievements/chapter_unlock_5.jpg',
    category: 'chapter',
  },

  // ============ 表现勋章 ============
  {
    id: 'perfect_discipline',
    name: '完美纪律',
    description: '总正确率达到 90% 及以上',
    imagePath: '/assets/achievements/perfect_discipline.jpg',
    category: 'performance',
  },
  {
    id: 'lightning_reflex',
    name: '神速反应',
    description: '平均答题时间低于 5 秒',
    imagePath: '/assets/achievements/lightning_reflex.jpg',
    category: 'performance',
  },

  // ============ 坚持勋章 ============
  {
    id: 'try_100',
    name: '全勤战将',
    description: '总尝试次数达到 100 次',
    imagePath: '/assets/achievements/try_100.jpg',
    category: 'persistence',
  },
]

/**
 * 根据当前进度 + 章节完成情况, 评估每个勋章是否解锁
 * 返回新解锁的勋章 ID 列表(用于弹提示)
 */
export interface AchievementEvalInput {
  progress: Record<string, Progress>
  totalAttempts: number
  correctRate: number                  // 0-1
  averageDecisionTimeMs: number
  chapterCardIds: Record<number, string[]>   // chapterId -> cardIds
  isChapterUnlocked: (chapterId: number) => boolean
}

export function evaluateAchievements(
  input: AchievementEvalInput,
  previouslyUnlocked: Set<string> = new Set()
): { newlyUnlocked: string[]; status: Record<string, boolean> } {
  const status: Record<string, boolean> = {}
  const newlyUnlocked: string[] = []

  // 允许 progress 缺省 (旧存档 + 全新用户场景)
  const progress: Record<string, Progress> = input.progress ?? {}

  const anyCardDone = Object.values(progress).some(p => p.attempts > 0)
  status['first_card'] = anyCardDone

  for (const chId of [1, 2, 3, 4, 5]) {
    const ids = input.chapterCardIds[chId] ?? []
    if (ids.length === 0) {
      status[`ch${chId}_master`] = false
      continue
    }
    const allMastered = ids.every(
      id => (progress[id]?.mastery ?? 0) >= 60
    )
    status[`ch${chId}_master`] = allMastered
  }

  status['chapter_unlock_5'] = input.isChapterUnlocked(5)
  status['perfect_discipline'] = input.totalAttempts >= 10 && input.correctRate >= 0.9
  status['lightning_reflex'] =
    input.totalAttempts >= 5 && input.averageDecisionTimeMs < 5000
  status['try_100'] = input.totalAttempts >= 100

  // 只返回"刚解锁"的 (本轮新解锁, 之前没解锁)
  for (const def of ACHIEVEMENTS) {
    if (status[def.id] && !previouslyUnlocked.has(def.id)) {
      newlyUnlocked.push(def.id)
    }
  }

  return { newlyUnlocked, status }
}

/**
 * 把已存储的 achievements 跟当前评估合并, 保留已解锁时间戳, 追加新解锁
 */
export function mergeAchievements(
  stored: Record<string, number> | undefined,
  newlyUnlocked: string[]
): Record<string, number> {
  const result: Record<string, number> = { ...(stored ?? {}) }
  const now = Date.now()
  for (const id of newlyUnlocked) {
    if (!result[id]) {
      result[id] = now
    }
  }
  return result
}